import { useEffect, useState } from "react";
import { FileText, FileSpreadsheet, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { appApi, authedFileUrl, inr, fmtDate, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function AdminInvoices() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => appApi.get("admin/invoices").then(setData).catch(() => setData({ invoices: [] }));
  useEffect(() => { load(); }, []);

  const markPaid = async (v, paid) => {
    setBusy(v.id);
    try {
      await appApi.post(`admin/invoices/${v.id}/paid`, { paid });
      toast.success(paid ? "Invoice marked paid." : "Invoice reopened.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const invoices = data?.invoices || [];

  return (
    <div data-testid="admin-invoices-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Invoices</h1>
        <p className="text-sm text-zinc-500 mt-2">All merchant invoices — download PDF/Excel, mark paid once the payment lands.</p>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[1020px]" data-testid="admin-invoices-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["Invoice", "Client", "Period", "Orders", "Taxable", "GST", "Total", "Status", "Files", "Action"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((v, i) => {
              const paid = String(v.status).toLowerCase() === "paid" || v.paid === true;
              return (
                <tr key={v.id || i} className="border-b border-zinc-100 last:border-0 hover:bg-acid/10 transition-colors duration-100" data-testid={`admin-invoice-row-${v.id || i}`}>
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs font-bold flex items-center gap-2">
                      <FileText className="w-4 h-4 text-acid" /> {v.number || v.id}
                    </p>
                    <p className="text-[10px] font-mono text-zinc-400 mt-1">{fmtDateTime(v.created_at)}{v.created_by ? ` · ${v.created_by}` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-700 font-semibold">{v.client_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{v.start && v.end ? `${fmtDate(v.start)} → ${fmtDate(v.end)}` : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.orders ?? "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.taxable != null ? inr(v.taxable) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.gst != null ? inr(v.gst) : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs font-bold">{v.total != null ? inr(v.total) : "—"}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={paid ? "paid" : "unpaid"} />
                    {v.paid_at && <p className="text-[10px] font-mono text-green-700 mt-1">Paid {fmtDate(v.paid_at)}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <a href={authedFileUrl(`invoices/${v.id}/pdf`)} target="_blank" rel="noreferrer" aria-label={`PDF for ${v.number}`} data-testid={`admin-invoice-pdf-${v.id || i}`}
                        className="p-2 border-2 border-obsidian bg-white hover:bg-acid hover:text-white transition-colors duration-150">
                        <FileText className="w-3.5 h-3.5" />
                      </a>
                      <a href={authedFileUrl(`invoices/${v.id}/xlsx`)} target="_blank" rel="noreferrer" aria-label={`Excel for ${v.number}`} data-testid={`admin-invoice-xlsx-${v.id || i}`}
                        className="p-2 border-2 border-obsidian bg-white hover:bg-acid hover:text-white transition-colors duration-150">
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => markPaid(v, !paid)} disabled={busy === v.id} data-testid={`admin-invoice-paid-${v.id || i}`}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-obsidian transition-colors duration-150 disabled:opacity-50 ${paid ? "bg-white text-zinc-500 hover:bg-paper" : "bg-acid text-white hover:bg-obsidian"}`}>
                      {paid ? <><RotateCcw className="w-3.5 h-3.5" /> Reopen</> : <><Check className="w-3.5 h-3.5" /> {busy === v.id ? "…" : "Mark paid"}</>}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data && invoices.length === 0 && (
          <p className="px-5 py-10 text-sm text-zinc-500" data-testid="admin-invoices-empty">No invoices raised yet — generate one from a merchant's rate card section.</p>
        )}
        {!data && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading invoices…</p>}
      </div>
    </div>
  );
}
